import React, { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { PdfPreviewModal } from "../components/PdfPreviewModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Printable wall poster asking for Google reviews, with writer's-block suggestions.
export default function ReviewPosterPage() {
  const [info, setInfo] = useState(null);
  const [logo, setLogo] = useState("");
  const [pdfDoc, setPdfDoc] = useState(null);

  const openPdf = async () => {
    try {
      const { data } = await axios.get(`${API}/site/review-poster.pdf`,
        { params: { origin: window.location.origin }, responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      setPdfDoc({ blob, url: URL.createObjectURL(blob) });
    } catch { alert("Could not build the PDF — please try again."); }
  };
  const closePdf = () => { if (pdfDoc?.url) URL.revokeObjectURL(pdfDoc.url); setPdfDoc(null); };

  useEffect(() => {
    axios.get(`${API}/site/review-info`).then((r) => setInfo(r.data));
    axios.get(`${API}/settings`).then((r) => setLogo(r.data?.logo_url || "")).catch(() => {});
  }, []);

  if (!info) return null;
  const suggestions = (info.suggestions || []).slice(0, 8);

  return (
    <div className="min-h-screen bg-white text-[#111] p-8 print:p-0" data-testid="review-poster-page">
      <style>{`@media print {
        .no-print, header { display: none !important; }
        @page { size: A4 portrait; margin: 12mm; }
        .review-poster-body { zoom: 0.92; }
      }`}</style>
      <div className="max-w-2xl mx-auto review-poster-body">
        <div className="no-print flex justify-end gap-2 mb-4">
          <button className="btn-secondary" onClick={openPdf} data-testid="review-poster-pdf-btn">⬇ Download PDF</button>
          <button className="btn-primary" onClick={() => window.print()} data-testid="review-poster-print-btn">🖨 Print poster</button>
        </div>
        {!info.review_url && (
          <div className="no-print rounded-xl border-2 border-amber-400 bg-amber-50 p-4 mb-4 text-sm" data-testid="review-poster-setup-hint">
            <b>Heads-up:</b> the QR below always works — it points to {window.location.origin}/review, which forwards people
            to your Google review box. Save your Google review link in <b>Operations Panel → Front Desk tab</b> to connect it
            (no reprint needed — printed posters update automatically).
          </div>
        )}

        <div className="border-4 border-black rounded-2xl p-8 text-center">
          {logo && <img src={logo} alt="logo" className="h-20 mx-auto mb-3 object-contain" data-testid="review-poster-logo" />}
          <div className="text-4xl tracking-[0.35em] text-[#D99E02] mb-2">★★★★★</div>
          <h1 className="text-4xl font-extrabold font-display uppercase tracking-tight m-0">Enjoyed your training?</h1>
          <p className="text-lg font-semibold mt-2 mb-6">A Google review is the best compliment you can give us — and it takes 30 seconds.</p>

          <div className="flex items-center justify-center mb-6">
            <QRCodeSVG value={`${window.location.origin}/review`} size={190} level="H" data-testid="review-poster-qr" />
          </div>
          <p className="font-display font-extrabold uppercase tracking-widest text-sm mb-6">Scan → Tap the stars → Done</p>

          <div className="text-left border-t-2 border-black pt-5">
            <div className="font-display font-extrabold uppercase tracking-wide mb-2">Don't know what to write? Short is perfect:</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {suggestions.map((s, i) => <div key={i}>"{s}"</div>)}
            </div>
            <p className="text-xs text-gray-600 mt-4">Or tell your own story — what you learned, how it felt, who you'd send here. Every word helps someone find us.</p>
          </div>
        </div>
        <p className="text-center text-sm font-bold mt-4">Thank you — it means more than you know. 🙏</p>
      </div>
      {pdfDoc && <PdfPreviewModal title="Google Review Poster — PDF" filename="google-review-poster.pdf"
        blob={pdfDoc.blob} url={pdfDoc.url} onClose={closePdf} />}
    </div>
  );
}
